import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import moment from 'moment';
import { useQuery } from 'hooks';
import { buildUrl } from 'utils';
import Can from 'components/Can';
import { debounce } from 'lodash';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import Status from 'components/Status';
import Priority from 'components/Priority';
import { AlarmsActions } from 'state/modules/alarms';
import { toQueryString, getDynamicLink } from 'utils';
import StatusIcon from 'components/Status/StatusIcon';
import { AlarmsTypeSelect } from 'components/AsyncFields';
import { AlarmStatusSelect, PrioritySelect } from 'components/DataFields';
import { Box, Grid, Paper, Button, Container, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

const LinkWrapper = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  '& a': {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  '& div': {
    whiteSpace: 'pre-wrap !important',
  },
}));

const AlarmsList = () => {
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [selectedAlarms, setSelectedAlarms] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);
  const list = useSelector((state) => state.alarms.list);
  const loading = useSelector((state) => state.alarms.loading);
  const paginationAlarms = useSelector((state) => state.alarms.paginationAlarms);
  const fetchedUpdateAlarmsStatusSuccess = useSelector(
    (state) => state.alarms.fetchedUpdateAlarmsStatusSuccess
  );

  const getSelectedConfigurations = (data) => {
    let configurations = Array.isArray(data) ? [...data] : [data];
    configurations = configurations.map((e) => ({ id: e.split('.')[0], name: e.split('.')[1] }));
    return configurations;
  };

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    status: 'open',
    ...urlParams,
    configurations: urlParams?.configurations
      ? getSelectedConfigurations(urlParams.configurations)
      : [],
  });

  /**
   * Table columns
   * Includes fleet and service groups
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: (row) => (
        <Typography onClick={() => handleRowClick(row)} sx={{ cursor: 'pointer' }}>
          <Box fontSize={13} component="span">
            A - {row.id}
          </Box>
        </Typography>
      ),
      grow: 0,
      sortable: true,
      sortableField: 'id',
    },
    {
      name: i18n.t('tableColumns.priority'),
      selector: (row) => (
        <Box>
          <Priority priority={row.current_priority} />
        </Box>
      ),
    },
    {
      name: i18n.t('tableColumns.type'),
      grow: 2,
      selector: (row) => (
        <Typography
          component="div"
          sx={{
            cursor: 'pointer',
            '& div': {
              whiteSpace: 'pre-wrap !important',
            },
          }}
          onClick={() => handleRowClick(row)}
        >
          <Box fontSize={13}>{row.name}</Box>
        </Typography>
      ),
    },
    {
      name: i18n.t('tableColumns.link_to'),
      selector: (row) => (
        <LinkWrapper>
          <Box fontSize={13}>
            {getDynamicLink(
              i18n.t(`common.${row.primary_object_type}`),
              row.primary_object_id,
              row.primary_object_type,
              '',
              true
            )}
          </Box>
        </LinkWrapper>
      ),
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.tasks'),
      selector: (row) => (
        <Box>
          {row?.tasks?.length > 0 ? (
            <>
              {row.tasks.map((task) => (
                <Typography key={task.id} component="div">
                  <Box
                    fontSize={13}
                    display="flex"
                    alignItems="center"
                    sx={row.overdue ? { color: '#FA5252' } : {}}
                  >
                    <StatusIcon status={task.status} />
                    {task.display_name}
                  </Box>
                </Typography>
              ))}
            </>
          ) : (
            <Typography component="div">
              <Box fontSize={13}>--</Box>
            </Typography>
          )}
        </Box>
      ),
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.creation_date'),
      selector: (row) => (
        <Typography component="div" sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(row)}>
          <Box fontSize={13} display="flex" alignItems="center">
            {moment(row.created_time).format(`${localeFormat} hh:mm A`)}
          </Box>
        </Typography>
      ),
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.status'),
      selector: (row) => (
        <Box>
          <Status status={row.status} statusKey="alarms_statuses" />
        </Box>
      ),
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row) => row.status === 'CLOSED',
      style: {
        opacity: 0.7,
      },
    },
  ];

  /**
   * Get vehicles grpups list
   * @param {object} params Request params
   */
  const fetchAlarms = useCallback(
    (params = {}) => {
      const _params = {
        ...params,
        actionable: true,
      };
      if (_params.status === 'open') {
        _params.active = true;
      } else if (_params.status === 'closed') {
        _params.active = false;
      }
      delete _params.status;
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(AlarmsActions.fetchAlarms(_params, { signal: abortController?.current?.signal }));
    },
    [dispatch]
  );

  const fetchAlarmsDebounced = useCallback(
    debounce((params) => fetchAlarms(params), 500),
    [fetchAlarms]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchAlarmsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchAlarmsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchAlarms(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (fetchedUpdateAlarmsStatusSuccess) {
      setClearSelectedRows(true);
      fetchAlarms(params);
    }
  }, [fetchAlarms, fetchedUpdateAlarmsStatusSuccess]);

  const handleParams = (params) => {
    setParams(params);
    const data = { ...params };

    if (data.configurations && Array.isArray(data.configurations)) {
      data.configurations = data.configurations.map((val) => `${val.id}.${val.name}`);
    }
    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && delete data[key]);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/alarms${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.sortableField,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
    fetchAlarms({ ...params, ...newParams });
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const _params = Object.assign({}, params);
    if (name === 'active') {
      delete _params.active;
      delete _params.status;
    }
    const filters = {
      page: 1,
      ..._params,
      ...(typeof value !== 'undefined' && { [name]: value }),
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => (filters[key] === undefined || filters[key] === '') && delete filters[key]
    );
    handleParams(filters);

    const data = { ...filters };

    if (data.active) {
      data.status = 'open';
    } else if (data.active === false) {
      data.status = 'closed';
    }

    handleParams(data);
  };

  /**
   * Handle selected row
   * @param {object} row Data of the row selected
   */
  const handleSelected = (state) => {
    setSelectedAlarms(state.selectedRows.map((row) => row.id));
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/alarms/${row?.id}`));
  };

  const bulkChangeAlarmsStatus = () => {
    const payload = {
      action: 'set_status',
      action_options: { status: 'CLOSED' },
      conditional_ids: selectedAlarms,
    };
    dispatch(AlarmsActions.fetchUpdateAlarmsStatus(payload));
  };

  return (
    <Layout title={i18n.t('alarms.title')}>
      <Container sx={{ minWidth: '100%', m: 0 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Grid container direction="row" justifyContent="space-between" spacing={2}>
              <Grid>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="bold" fontSize={24} data-cy="title">
                    {i18n.t('alarms.title')}
                  </Box>
                </Typography>
              </Grid>
              <Can I="update" a="conditional_instance">
                <Grid>
                  <Button
                    onClick={bulkChangeAlarmsStatus}
                    variant="contained"
                    sx={{
                      textTransform: 'capitalize',
                      mr: 2,
                    }}
                    color="primary"
                    data-cy="change-alarms-status-button"
                    disabled={!selectedAlarms.length}
                  >
                    {i18n.t('alarms.close_alarms')}
                  </Button>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ paddingBottom: '20px' }}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
                <PrioritySelect
                  margin="normal"
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'priority',
                    value: params.priority || '',
                  }}
                  label={i18n.t('alarms.priority')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
                <AlarmStatusSelect
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'active',
                    value: params.status,
                  }}
                  label={i18n.t('alarms.status')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3, lg: 4 }}>
                <AlarmsTypeSelect
                  form={{
                    setFieldValue: search,
                  }}
                  field={{
                    name: 'configurations',
                    value: params.configurations,
                  }}
                  label={i18n.t('alarms.types')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper
              elevation={2}
              sx={(theme) => ({
                boxShadow: `0px 3px 6px ${alpha(theme.palette.common.black, 0.1)}`,
              })}
            >
              <Table
                data={list}
                columns={columns}
                loading={loading}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                selectableRows={true}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                clearSelectedRows={clearSelectedRows}
                totalRows={paginationAlarms?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                handleSelectedRowsChange={handleSelected}
                conditionalRowStyles={conditionalRowStyles}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default AlarmsList;
